import React from 'react';

import { TRACKING_OPT_OUT } from '@/utils/analytics/trackingOptOut';
import { Skeleton } from '../../../../components';
import type { StatsDelta } from '../../../../mock-data/types';
import { Delta } from '../../../stats/components';
import { GlyphBadge } from './GlyphBadge';
import type { ShareGlyphName } from './ShareGlyph';
import { Grid, Tile, GlyphSlot, Name, ValueRow, Value, Note, Track, Fill, Empty } from './ShareTiles.styles';

export type ShareTile = {
  key: string;
  glyph: ShareGlyphName;
  /** What the tile is, in plain words (already translated, or a brand name). */
  name: string;
  /** The tile's share, formatted ("85%"). */
  value: string;
  /** A quieter figure under it ("60 visitors"). */
  note?: string;
  /** Bar length, 0..1 of the whole. */
  share: number;
  /** Its visitors against the period before (null: not compared). */
  change?: StatsDelta | null;
  /** Its visitors as a number (not drawn: for the exported report). */
  count?: number;
};

export type ShareTilesProps = {
  items: ShareTile[];
  loading?: boolean;
  error?: boolean;
  /** Shown when there is nothing to split (already translated). */
  emptyText: string;
  errorText: string;
  /** Placeholder tiles while loading. */
  placeholderTiles?: number;
};

/** Tiles per row: two, whatever their number. The cards sit in the tab's narrow column, where three or four side by
 *  side squeeze their names and changes. */
const columnsFor = (): number => 2;

/** A whole split into side-by-side tiles, each with its glyph, share and count: devices, operating systems. */
export const ShareTiles = ({
  items,
  loading = false,
  error = false,
  emptyText,
  errorText,
  placeholderTiles = 2,
}: ShareTilesProps): React.ReactElement => {
  if (loading) {
    return (
      <Grid aria-busy="true" $cols={columnsFor()}>
        {Array.from({ length: placeholderTiles }, (_, i) => (
          <Tile key={i}>
            <Skeleton width={40} height={40} radius={999} />
            <Skeleton width="60%" height={14} radius={6} />
            <Skeleton width="45%" height={22} radius={6} />
            <Skeleton block height={4} radius={999} />
          </Tile>
        ))}
      </Grid>
    );
  }
  if (error) return <Empty text={errorText} tag="span" raw />;
  if (items.length === 0) return <Empty text={emptyText} tag="span" raw />;
  return (
    <Grid $cols={columnsFor()} {...TRACKING_OPT_OUT}>
      {items.map((item) => (
        <Tile key={item.key}>
          <GlyphSlot>
            <GlyphBadge name={item.glyph} />
          </GlyphSlot>
          <Name text={item.name} tag="span" raw title={item.name} />
          <ValueRow>
            <Value text={item.value} tag="span" raw />
            {item.change ? <Delta delta={item.change} size={10} /> : null}
          </ValueRow>
          {item.note ? <Note text={item.note} tag="span" raw /> : null}
          <Track aria-hidden="true">
            <Fill $share={item.share} />
          </Track>
        </Tile>
      ))}
    </Grid>
  );
};

export default ShareTiles;
